import { useEffect, useState } from 'react';
import colors from '../../../constants/colors.constants';
import Typo from '../../../components/atom/Typo';
import strings from '../../../constants/strings.constants';
import Button from '../../../components/atom/Button';
import ReservationConfirmModal from './components/ReservationConfirmModal';
import { ReactComponent as IconBack } from '../../../assets/arrow-back.svg';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTER_PATHS } from '../../../routes/routes.constatns';
import useAuthStore from '../../../store/useAuthStore';
import SelectV2 from '../../../components/atom/SelectV2';
import { SingleValue } from 'react-select';
import TextArea from '../../../components/atom/TextArea';
import { useQuery } from 'react-query';
import { fetchConsultingCategories } from '../../../apis/consulting-categories';
import { fetchRecentReservations } from '../../../apis/reservations';
import moment from 'moment';
import Spinner from '../../../components/atom/Spinner';
import useLanguageCodeStore from '../../../store/useLanguageCodeStore';
import { sendBeginCheckoutEvent } from '../../../utils/ga-utils';

export interface ReservationPageProps {
  reservationType?: 'CONSULTING' | 'REVISIT' | 'TREATMENT';
  treatmentAutoSelect?: boolean;
}

function ReservationPage(props: ReservationPageProps) {
  const { reservationType = 'CONSULTING', treatmentAutoSelect } = props;
  const { treatmentCategories = [], partner, customer } = useAuthStore();
  const location = useLocation();
  const [reservationConfirmModalVisible, setReservationConfirmModalVisible] =
    useState(false);
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] =
    useState<SingleValue<{ label: string; value: number }>>(null);
  const [selectedChildCategory, setSelectedChildCategory] =
    useState<SingleValue<{ label: string; value: number }>>(null);
  const [selectedRecentReservationDate, setSelectedRecentReservationDate] =
    useState<string | null>(null);
  const [consultingMemo, setConsultingMemo] = useState('');
  const [selectedConsultingCategories, setSelectedConsultingCategories] =
    useState<string[]>([]);
  const { languageCode } = useLanguageCodeStore();
  const customLabel =
    partner?.productViewConfig?.mimoWebViewConfig?.customLabel;

  const {
    data: consultingCategories = [],
    isLoading: isLoadingConsultingCategories,
  } = useQuery({
    queryKey: ['FETCH_CONSULTING_CATEGORIES'],
    queryFn: async () => {
      if (partner) {
        return fetchConsultingCategories(partner.id);
      }
      return [];
    },
    retry: false,
    enabled:
      !!partner &&
      (reservationType === 'TREATMENT' || reservationType === 'REVISIT'),
    refetchOnWindowFocus: false,
  });

  const isKoreanLang = languageCode === 'ko' || !languageCode;

  const {
    data: recentReservations = [],
    isLoading: isLoadingRecentReservation,
  } = useQuery({
    queryKey: [`FETCH_RECENT_RESERVATIONS_${customer?.token}`],
    queryFn: async () => {
      if (customer) {
        return fetchRecentReservations(customer.token);
      }
      return [];
    },
    retry: false,
    enabled: !!partner && reservationType === 'REVISIT',
    refetchOnWindowFocus: false,
  });

  const canGoNext = () => {
    if (
      reservationType === 'REVISIT' &&
      !recentReservations.some(
        ({ reservationDate }) =>
          reservationDate === selectedRecentReservationDate,
      )
    ) {
      return false;
    }
    // Revisit/treatment extra requests remain memo-only, not consultation intent.
    if (reservationType !== 'CONSULTING') return true;

    if (partner) {
      const { homepageConfig } = partner;

      if (homepageConfig) {
        const { isRequiredConsultationReservationTreatmentCategory } =
          homepageConfig;
        if (
          isRequiredConsultationReservationTreatmentCategory &&
          treatmentCategories.length > 0
        ) {
          if (selectedCategory) {
            const selected = treatmentCategories.find(({ id }) => {
              return id === selectedCategory.value;
            });
            if (selected && selected.childTreatmentCategories.length === 0) {
              return true;
            }
          }

          return !!selectedChildCategory;
        }

        return true;
      }
    }
    return true;
  };

  const getRecentReservationOptions = () => {
    return recentReservations.map(({ reservationDate }) => {
      return {
        label: moment(reservationDate).format('YY.MM.DD (dd) HH:mm'),
        value: reservationDate,
      };
    });
  };

  const shouldRenderTreatmentOptions = () => {
    return treatmentCategories.length > 0;
  };

  const getParentTreatmentOptions = () => {
    return treatmentCategories.map(({ id, name }) => {
      return {
        label: name,
        value: id,
      };
    });
  };

  const getChildTreatmentOptions = (parent?: {
    label: string;
    value: number;
  }) => {
    const parentCategory = parent ?? selectedCategory;
    if (parentCategory) {
      const matched = treatmentCategories.find(({ id }) => {
        return `${id}` === `${parentCategory.value}`;
      });

      if (matched) {
        return matched.childTreatmentCategories.map(({ id, name }) => {
          return {
            label: name,
            value: id,
          };
        });
      }
    }

    return [];
  };

  const noRecentReservation = () => {
    if (!isLoadingRecentReservation || isLoadingConsultingCategories) {
      return recentReservations.length === 0;
    }
    return false;
  };

  const shouldRenderExtraConsultingCategories = () => {
    if (partner) {
      let useExtraExtraConsultingCategory = false;
      const { chatBotConfig } = partner;
      if (chatBotConfig) {
        useExtraExtraConsultingCategory =
          chatBotConfig.useExtraConsultingCategory || false;
      }

      return (
        useExtraExtraConsultingCategory &&
        (reservationType === 'TREATMENT' || reservationType === 'REVISIT') &&
        consultingCategories.length > 0
      );
    }

    return false;
  };

  const getSelectedCategory = () => {
    if (selectedChildCategory) {
      return selectedChildCategory.value;
    } else if (selectedCategory) {
      return selectedCategory.value;
    }
  };

  const getHeaderTitle = () => {
    if (reservationType === 'CONSULTING') {
      return strings.consultingReservationButtonTitle;
    }
    if (reservationType === 'TREATMENT') {
      return strings.treatmentReservationButtonTitle;
    }
    if (reservationType === 'REVISIT') {
      return strings.revisitReservationButtonTitle;
    }
    return strings.consultingReservationButtonTitle;
  };

  const handleSelectRecentReservation = (selectedDate: string | null) => {
    const recent = recentReservations.find(
      ({ reservationDate }) => reservationDate === selectedDate,
    );
    setSelectedRecentReservationDate(recent?.reservationDate ?? null);
    setSelectedCategory(null);
    setSelectedChildCategory(null);
    if (!recent?.parentTreatmentCategory) return;

    const parent = getParentTreatmentOptions().find(
      ({ label }) => label === recent.parentTreatmentCategory,
    );
    if (!parent) return;
    setSelectedCategory(parent);
    if (!recent.childTreatmentCategory) return;

    const child = getChildTreatmentOptions(parent).find(
      ({ label }) => label === recent.childTreatmentCategory,
    );
    if (child) setSelectedChildCategory(child);
  };

  useEffect(() => {
    if (!treatmentAutoSelect) {
      return;
    }

    if (treatmentCategories.length <= 0) {
      return;
    }

    setSelectedCategory({
      label: treatmentCategories[0].name,
      value: treatmentCategories[0].id,
    });
  }, []);

  if (isLoadingRecentReservation || isLoadingConsultingCategories) {
    return (
      <div className="h-full flex flex-col items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div
        className="min-h-[48px] flex items-center justify-center relative"
        style={{
          borderBottom: `1px solid ${colors.defaultBorder}`,
        }}
      >
        <div
          style={{
            padding: '5px',
            cursor: 'pointer',
            position: 'absolute',
            left: 16,
          }}
        >
          <IconBack
            onClick={() => {
              navigate(
                {
                  pathname: ROUTER_PATHS.RESERVATION,
                  search: location.search,
                },
                {
                  replace: true,
                },
              );
            }}
          />
        </div>
        <Typo type="b-16">{getHeaderTitle()}</Typo>
      </div>
      <div
        className="flex flex-col flex-1 overflow-y-auto gap-3"
        style={{
          paddingBottom: 20,
          backgroundColor: colors.greyHover,
        }}
      >
        {reservationType === 'REVISIT' && (
          <div className="bg-white py-6 px-5 flex flex-col gap-4">
            <Typo type="m-16">
              <div className="flex gap-1">
                {strings.selectRecentReservationTitle}
                <span
                  style={{
                    color: colors.slate500,
                    fontWeight: 400,
                  }}
                >
                  {strings.selectRecentReservationTitlePostfix}
                </span>
              </div>
            </Typo>
            <SelectV2
              isDisabled={
                isLoadingRecentReservation || recentReservations.length === 0
              }
              value={
                getRecentReservationOptions().find(
                  (option) => option.value === selectedRecentReservationDate,
                ) ?? null
              }
              placeholder={
                noRecentReservation()
                  ? strings.noRecentReservation
                  : strings.selectRecentReservationTitle
              }
              options={getRecentReservationOptions()}
              onChange={(option) => {
                handleSelectRecentReservation(option ? option.value : null);
              }}
            />
          </div>
        )}
        {shouldRenderTreatmentOptions() && (
          <div className="bg-white py-6 px-5 flex flex-col gap-4">
            <Typo type="m-16">
              {customLabel?.selectTreatmentCategoryDesc ||
                strings.selectTreatmentCategoryDesc}
            </Typo>
            <SelectV2
              isClearable
              value={selectedCategory}
              placeholder={
                customLabel?.primaryTreatmentCategoryPlaceholder ||
                strings.primaryTreatmentCategoryPlaceholder
              }
              options={getParentTreatmentOptions()}
              onChange={(value) => {
                setSelectedCategory(value);

                let firstChildCategory = null;
                if (value) {
                  const matched = treatmentCategories.find(
                    ({ id }) => id === value.value,
                  );
                  if (matched) {
                    const { childTreatmentCategories = [] } = matched;
                    if (childTreatmentCategories.length > 0) {
                      const [firstChild] = childTreatmentCategories;
                      const { id, name } = firstChild;
                      firstChildCategory = {
                        label: name,
                        value: id,
                      };
                    }
                  }
                }
                setSelectedChildCategory(firstChildCategory);
              }}
            />
            <SelectV2
              isDisabled={
                !selectedCategory || getChildTreatmentOptions().length === 0
              }
              value={selectedChildCategory}
              placeholder={
                customLabel?.secondaryTreatmentCategoryPlaceholder ||
                strings.secondaryTreatmentCategoryPlaceholder
              }
              options={getChildTreatmentOptions()}
              onChange={(value) => {
                setSelectedChildCategory(value);
              }}
            />
          </div>
        )}

        {shouldRenderExtraConsultingCategories() && isKoreanLang && (
          <div className="bg-white py-6 px-5 flex flex-col gap-4">
            <Typo type="m-16">
              {strings.extraConsultingCategoryTitle}
              <span
                style={{
                  marginLeft: 4,
                  color: colors.slate500,
                  fontWeight: 400,
                }}
              >
                {strings.extraConsultingCategoryTitlePostFix}
              </span>
            </Typo>
            <div className="flex gap-1 flex-wrap">
              {consultingCategories.map(({ id, name }) => {
                const isSelected = !!selectedConsultingCategories.find(
                  (selectedName) => {
                    return selectedName === name;
                  },
                );
                return (
                  <Button
                    size="small"
                    style={{
                      height: 38,
                      background: isSelected ? colors.slate800 : 'white',
                      color: isSelected ? 'white' : colors.slate500,
                      fontWeight: isSelected ? 500 : 300,
                      border: '1px solid',
                      borderColor: isSelected
                        ? colors.slate800
                        : colors.defaultBorder,
                    }}
                    onClick={() => {
                      if (isSelected) {
                        setSelectedConsultingCategories(
                          selectedConsultingCategories.filter(
                            (selectedName) => selectedName !== name,
                          ),
                        );
                      } else {
                        setSelectedConsultingCategories([
                          ...selectedConsultingCategories,
                          name,
                        ]);
                      }
                    }}
                    key={id}
                  >
                    {name}
                  </Button>
                );
              })}
            </div>
          </div>
        )}

        <div className="bg-white py-6 px-5 flex flex-col gap-4">
          <Typo type="m-16">
            {customLabel?.consultingMemo || strings.consultingMemo}
          </Typo>
          <TextArea
            placeholder={
              customLabel?.consultingMemoPlaceholder ||
              strings.consultingMemoPlaceholder
            }
            value={consultingMemo}
            onChange={(value) => {
              setConsultingMemo(value);
            }}
          />
        </div>
      </div>
      <div
        style={{
          padding: '16px 20px',
          boxShadow: '0px -4px 30px 0px #0000001f',
        }}
      >
        <Button
          style={{
            width: '100%',
          }}
          disabled={!canGoNext()}
          onClick={() => {
            setReservationConfirmModalVisible(true);
            sendBeginCheckoutEvent([]);
          }}
        >
          {strings.next}
        </Button>
      </div>
      <ReservationConfirmModal
        reservationType={reservationType}
        isConsultingReservation={reservationType === 'CONSULTING'}
        consultingCategories={selectedConsultingCategories}
        consultingMemo={consultingMemo}
        treatmentCategoryId={getSelectedCategory()}
        onCancel={() => {
          setReservationConfirmModalVisible(false);
        }}
        visible={reservationConfirmModalVisible}
        parentCategoryName={selectedCategory?.label}
        childCategoryName={selectedChildCategory?.label}
      />
    </div>
  );
}

export default ReservationPage;
